<template>
	<Hyperlink
		class="_lets-talk-cta relative inline-block"
		:to="to"
		:modal="modal"
		data-hitarea
		@mouseenter="onMouseEnter"
		@mouseleave="onMouseLeave"
	>
		<div class="background absolute inset-0 m-t" />
		<div ref="labelRef" class="label relative flex align-baseline m-c -p-small">
			{{ label }}
			<span v-if="showQuestion" ref="questionRef" class="m-t absolute right-0 -p-small">?</span>
		</div>
	</Hyperlink>
</template>

<script setup>
import { gsap } from 'gsap'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useScreen } from 'vue-screen'
import { HyperLink as Hyperlink } from '@/components/core'

const emptyFunction = () => {}

// Usage:
// <LetsTalkCta /> keeps the default "Let's talk?" modal CTA.
// <LetsTalkCta label="Visit site" :to="{ type: 'external', url: 'https://example.com' }" :show-question="false" :modal="false" /> renders a generic CTA without the question animation.
const props = defineProps({
	label: { type: String, default: "Let's talk" },
	to: {
		type: Object,
		default: () => ({
			type: 'external',
			url: 'https://burocratik.com/lets-talk',
		}),
	},
	showQuestion: { type: Boolean, default: true },
	modal: { type: Boolean, default: true },
})

const screen = useScreen()
const labelRef = ref(null)
const questionRef = ref(null)

const onMouseEnter = computed(() => {
	if (screen.touch || !props.showQuestion) {
		return emptyFunction
	}

	return () => {
		gsap.killTweensOf([labelRef.value, questionRef.value])
		gsap.to(labelRef.value, { duration: 0.2, ease: 'power2.inOut', paddingRight: '1.7em' })
		gsap.to(questionRef.value, { duration: 0.2, delay: 0.19, ease: 'power2.out', yPercent: 0 })
		gsap.to(labelRef.value, {
			duration: 0.12,
			delay: 0.5,
			ease: 'power2.out',
			paddingRight: '0.54em',
		})
	}
})

const onMouseLeave = computed(() => {
	if (screen.touch || !props.showQuestion) {
		return emptyFunction
	}

	return () => {
		gsap.killTweensOf([labelRef.value, questionRef.value])
		gsap.to(questionRef.value, { duration: 0.3, delay: 0, ease: 'power2.inOut', yPercent: 110 })
		gsap.to(labelRef.value, { duration: 0.25, delay: 0.1, ease: 'power2.in', paddingRight: 0 })
	}
})

onMounted(() => {
	if (questionRef.value) {
		gsap.set(questionRef.value, { yPercent: 110 })
	}
})

onBeforeUnmount(() => {
	gsap.killTweensOf([labelRef.value, questionRef.value].filter(Boolean))
})
</script>

<style lang="scss" scoped>
@use '../atoms.scss';

._lets-talk-cta {
	align-self: end;
	justify-self: end;

	padding: rs(4, 4) rs(16, 16);

	border-radius: 0.9em;
	color: $black;

	overflow: hidden;

	.-p-small {
		color: $black;
	}

	& > .background {
		border-radius: inherit;
		background-color: $white;

		transform: scale(1);
		transition: transform 0.3s ease('power2', 'out');
	}

	&:active > .background {
		transform: scale(0.95);
	}

	.label {
		will-change: padding-right;
	}
}
</style>
